<template>
  <ul :class="$style.list" ref="list">
    <random-image-item
      v-for="(image, index) in list"
      :key="`image-${index}`"
      :image="image"
      :class="$style.image"
      :position="imagePosition(index)"
      :dimension="imageDimension(index)"
      :container="container"
      :list-index="index"
      :clicked-index="clickedIndex"
      :item-clicked="itemClicked"
      @click.native="setItem(index)"
    />
  </ul>
</template>

<script>
import RandomImageItem from '@/components/random-image-item'
import { mapState } from 'vuex'
import { debounce } from 'lodash'

export default {
  components: { RandomImageItem },
  props: {
    list: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      container: {
        width: 0,
        height: 0
      },
      positions: [],
      gutter: 20,
      clickedIndex: null,
      itemClicked: false
    }
  },
  computed: {
    ...mapState(['isDesktop']),
    featuredImages() {
      let images = []
      this.list.forEach(image => {
        const imageSize = this.imageSize(image)
        images.push(imageSize)
      })
      return images
    }
  },
  methods: {
    setItem(index) {
      this.clickedIndex = index
      this.itemClicked = true
    },
    resetItem() {
      this.clickedIndex = null
      this.itemClicked = false
    },
    getContainerDimension() {
      const ref = this.$refs.list
      const rect = ref.getBoundingClientRect()
      this.container = {
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        x: rect.x,
        y: rect.y
      }
    },
    // gutter() {
    //   return this.isDesktop ? 20 : 10
    // },
    imageSize(image) {
      const factor = this.isDesktop ? 2 : 1
      const min = this.container.width / (5 * factor)
      const max = this.container.width / (2 * factor)
      const width = this.getRandomInt(min, max)
      const height = Math.ceil(width / image.ratio)

      return {
        width: width,
        height: height
      }
    },
    imagePosition(index) {
      const position = this.positions[index]
      if (!position) return
      return {
        top: position.y,
        left: position.x
      }
    },
    imageDimension(index) {
      const dimension = this.featuredImages[index]
      if (!dimension) return
      return {
        width: dimension.width
      }
    },
    calculatePositions(images) {
      // Whenever we call calculatePositions(), first clear existing positions
      this.positions = []

      images.forEach(image => {
        let maxAttempts = images.length * 20
        let imagePlaced = false
        let available = false

        // Loop runs as long as image couldn’t be placed AND max attempts haven’t been reached
        while (!imagePlaced && maxAttempts > 0) {
          // Get random x and y coordinates that cannot exceed the container dimension
          const x = this.getRandomInt(
            this.gutter,
            this.container.width - image.width - this.gutter
          )
          const y = this.getRandomInt(
            this.isDesktop ? this.gutter * 3 : this.gutter * 5,
            this.container.height - image.height - this.gutter
          )

          // Create a position object to more easily pass it to a function
          const pos = {
            x: x,
            y: y,
            dx: x + image.width,
            dy: y + image.height
          }

          // Check if position is not yet occupied
          available = this.positionAvailable(pos, this.positions)

          if (available) {
            // If position still available, add it to positions
            this.positions.push(pos)
            // ...and let while loop know that image was placed successfully
            imagePlaced = true
          } else {
            // Otherwise decrease maxAttempts
            maxAttempts -= 1
          }
        }

        // Make sure to always add an item to positions in order to keep array indexes with featuredItems in sync
        if (!available) this.positions.push(null)
      })
    },
    positionAvailable(pos, positions) {
      // Based on https://www.geeksforgeeks.org/find-two-rectangles-overlap/
      const result = positions.find(existingPos => {
        // If it’s an "empty" existingPos, then return
        if (existingPos === null) return

        // If one rectangle is on left side of other
        if (
          pos.x > existingPos.dx + this.gutter ||
          existingPos.x > pos.dx + this.gutter
        ) {
          return false
        }

        // If one rectangle is above other
        if (
          pos.y > existingPos.dy + this.gutter ||
          existingPos.y > pos.dy + this.gutter
        ) {
          return false
        }

        return true
      })

      // If the condition didn’t return any results (= undefinded), return true (= position is available)
      return result === undefined ? true : false
    },
    getRandomInt(min, max) {
      min = Math.ceil(min)
      max = Math.floor(max)
      return Math.floor(Math.random() * (max - min + 1)) + min
    },
    onResize: debounce(function() {
      this.getContainerDimension()
      this.calculatePositions(this.featuredImages)
    }, 450)
  },
  mounted() {
    this.getContainerDimension()
    this.calculatePositions(this.featuredImages)
    window.addEventListener('resize', this.onResize)
  },
  activated() {
    this.resetItem()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  }
}
</script>

<style lang="scss" module>
.list {
  position: relative;
  height: 100%;
}
</style>
